import { GENDER } from "core/consts";
import { defaultsDeep } from "lodash";
import { DeepNullableObj, EncryptedField, PatientProfile } from "../types";
import { MockFn, createMock } from "./index";

const toEncryptedNullField = (): EncryptedField => {
  return {
    content: null,
    decrypted: null,
    iv: null,
    seald_content: null,
  };
};

const mockPatientProfile: MockFn<Partial<DeepNullableObj<PatientProfile>>> = (
  args,
) =>
  defaultsDeep(
    args,
    createMock(
      "PatientProfile",
      args,
      (): DeepNullableObj<PatientProfile> => ({
        __typename: "PatientProfile",
        admission_date: null,
        age: null,
        age_interval: { max: 29, min: 18 },
        barrier_free: null,
        barrier_free_description: null,
        birth_date: {
          content: null,
          decrypted: null,
          iv: null,
          seald_content: null,
        },
        care_duration_in_days: null,
        communication: {
          guardian_contact_information: null,
          guardian_requested: null,
          guardians: null,
          has_guardian: null,
          patient_cannot_communicate_in_german: null,
          patient_is_contact: null,
          patient_is_contact_description: null,
          patient_language: null,
          patient_uses_sign_language: null,
          relatives: null,
          relatives_available: null,
          relatives_description: null,
        },
        direct_transfer_necessary: null,
        discharge_date: null,
        financing: {
          benefit_entitlement_group: {
            available: null,
            description: null,
          },
          carelevel: {
            expedited_request: null,
            has_applied: null,
            higher_level: null,
            level: null,
          },
          insurance: {
            config: {
              use_vzd_kim_address: null,
            },
            id: null,
            ik_number: null,
            kim_address: null,
            name: null,
          },
          insurance_additional_information: null,
          insurance_id: null,
          insurance_number: {
            content: null,
            decrypted: null,
            iv: null,
            seald_content: null,
          },
          insurance_status: null,
          more_information: null,
          patient_has_health_insurance_state: null,
          payout: null,
          preventative_care: null,
          private_payment_bool: null,
          public_care_insurance_status: null,
          reimbursment_for_help_at_home: null,
          reimbursment_secured: null,
          relief_services: null,
          short_term_care: null,
          social_help_recipient_bool: null,
        },
        first_name: {
          content: null,
          decrypted: null,
          iv: null,
          seald_content: "encrypted-content",
        },
        gender: GENDER.FEMALE,
        general_practitioner: {
          city: toEncryptedNullField(),
          contact_details: null,
          first_name: toEncryptedNullField(),
          honorific_prefix: toEncryptedNullField(),
          honorific_suffix: toEncryptedNullField(),
          house_number: toEncryptedNullField(),
          job_title: toEncryptedNullField(),
          last_name: toEncryptedNullField(),
          name: null,
          phone: toEncryptedNullField(),
          street: toEncryptedNullField(),
          zipcode: toEncryptedNullField(),
        },
        has_patient_consent: null,
        height: {
          content: null,
          decrypted: null,
          iv: null,
          seald_content: null,
        },
        height_interval: { min: null },
        interested_long_term_stay: false,
        last_name: {
          content: null,
          decrypted: null,
          iv: null,
          seald_content: "encrypted-content",
        },
        lift_available_description: null,
        lift_available_state: null,
        living_situation: null,
        living_situation_other: null,
        placement_order: {
          exhibiting_state: null,
          status: null,
          type: null,
        },
        preferences: {
          accompanying_person: null,
          accompanying_person_description: null,
          barrier_free_room: null,
          room_type: null,
          specialist_doctor: null,
        },
        psychiatrist: {
          city: toEncryptedNullField(),
          first_name: toEncryptedNullField(),
          honorific_prefix: toEncryptedNullField(),
          honorific_suffix: toEncryptedNullField(),
          house_number: toEncryptedNullField(),
          job_title: toEncryptedNullField(),
          last_name: toEncryptedNullField(),
          phone: toEncryptedNullField(),
          street: toEncryptedNullField(),
          zipcode: toEncryptedNullField(),
        },
        unsafe_current_domestic_situation: null,
        unsafe_current_domestic_situation_state: 1,
        weight: {
          content: null,
          decrypted: null,
          iv: null,
          seald_content: null,
        },
        weight_interval: { max: null, min: null },
      }),
    ),
  );

export default mockPatientProfile;
